import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Heading,
  IconButton,
  LinkBox,
  LinkOverlay,
  Popover,
  Portal,
  RadioGroup,
  Text,
  VStack
} from "@chakra-ui/react"
import { ChevronRightIcon, SparklesIcon } from "@pathwright/pathicons"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import Link, {
  LinkRelativeTo,
  LinkTo
} from "@pathwright/ux/src/components/link/Link"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import moment from "moment"
import { Fragment, useMemo, useState } from "react"
import { Link as ReactRouterLink, useRouteMatch } from "react-router-dom"
import {
  CoverPageOfferFragment as CoverPageOffer,
  OfferAccessState,
  OfferScope
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { AUGMENTATION_PANEL_QUERY_PARAM } from "../utils/urls"
import CoverPageDivider from "./CoverPageDivider"
import CoverPagePathOfferButton from "./CoverPagePathOfferButton"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageIsEditable } from "./access"
import CoverPageSectionTrigger from "./design/CoverPageSectionTrigger"
import { ImmersiveTriggerButton } from "./immersive/CoverPageImmersive"
import { offerShowsBottomMetaRow, useCoverPageTranslate } from "./utils"

const dividerHeight = 21 // CoverPageDivider height

export type OfferButtonRenderProps = Partial<ButtonProps> & {
  to?: LinkTo
  relativeTo?: LinkRelativeTo
  href?: string
  target?: string
  rel?: string
}

export const offerButtonProps: OfferButtonRenderProps = {
  variant: "solid",
  colorPalette: "brand",
  textDecoration: "none",
  fontWeight: "700",
  size: "xl",
  _hover: { textDecoration: "none", opacity: 0.9 }
}

export const OfferButton = ({
  offer,
  selectedOptionIndex,
  buttonProps: buttonPropsOverride,
  renderButton
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  buttonProps?: OfferButtonRenderProps
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
}) => {
  const { url } = useRouteMatch()
  const { t } = useCoverPageTranslate()
  const { immersiveActive, setOfferConversionIntentId } = useCoverPageContext()
  const slug = useCoverPageContext((v) => v.paintResource?.slug)
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasAccess = offer.access.state === OfferAccessState.Active
  const isUnavailable = offer.access.state === OfferAccessState.Unavailable

  const buttonProps = useMemo(() => {
    const props: OfferButtonRenderProps = {
      children:
        selectedOption.button_label ||
        t("offer.continue", { defaultValue: "Continue" }),
      w: "100%",
      className: "CoverPageOfferButton",
      ...offerButtonProps,
      ...buttonPropsOverride
    }

    if (hasAccess) {
      if (!slug) {
        props.disabled = true
      } else {
        props.as = ReactRouterLink // Link: TODO: fix odd styles when using Link
        props.to = `/library/${slug}/${offer.cohort_id!}/path/`
        props.children = (
          <>
            {selectedOption.button_label ||
              t("offer.continue", { defaultValue: "Continue" })}
            <ChevronRightIcon />
          </>
        )
      }
    } else if (isUnavailable) {
      props.disabled = true
    } else {
      props.relativeTo = `?${AUGMENTATION_PANEL_QUERY_PARAM}=access/offer/${offer.id}/`

      // Pass the selected payment type so RegisterFlow can choose in-plan vs
      // one-off conversion. This is required even for a single option: an
      // active subscriber sees one "included in your subscription" option,
      // and omitting paymentType makes RegisterFlow default to the one-off
      // Stripe Checkout path.
      if (selectedOption.payment_type) {
        props.relativeTo = `${
          props.relativeTo
        }${selectedOption.payment_type.toLowerCase()}/`
      }
    }

    props.onPointerDown = () => {
      // Set conversion intent before the Link navigates so the panel does not
      // mount with attemptToConvert=false (Register CTA, no auto-submit).
      setOfferConversionIntentId(offer.id || null)
    }
    props.onClick = (e: React.MouseEvent) => {
      // Prevent the card wrapper's onClick (which opens the offer immersive route)
      // from hijacking this button's navigation to /register.
      e.stopPropagation()

      setOfferConversionIntentId(offer.id || null)
    }

    return props
  }, [
    hasAccess,
    isUnavailable,
    offer,
    immersiveActive,
    url,
    buttonPropsOverride,
    slug,
    selectedOption.button_label,
    selectedOption.payment_type,
    setOfferConversionIntentId,
    t
  ])

  if (offer.scope === OfferScope.Path) {
    return (
      <CoverPagePathOfferButton
        offer={offer}
        renderButton={renderButton}
        {...buttonProps}
      />
    )
  }

  const button =
    "relativeTo" in buttonProps ? (
      <Button asChild {...buttonProps}>
        <Link relativeTo={buttonProps.relativeTo}>{buttonProps.children}</Link>
      </Button>
    ) : (
      <Button {...buttonProps} />
    )

  return renderButton ? renderButton(buttonProps) : button
}

type OfferPricingProps = {
  offer: CoverPageOffer
  reserveMultipleOptionsHeight?: boolean
}

const OfferPricing = ({
  offer,
  reserveMultipleOptionsHeight
}: OfferPricingProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0)

  const handleChange = (idx: number) => {
    setSelectedOptionIndex(idx)
  }

  return (
    <VStack gap="5px" align="center" className="OfferPricing" w="100%">
      <OfferMultipleOptions
        offer={offer}
        selectedOptionIndex={selectedOptionIndex}
        onChangeSelectedOptionIndex={handleChange}
        reserveMultipleOptionsHeight={reserveMultipleOptionsHeight}
      />
      <OfferButton offer={offer} selectedOptionIndex={selectedOptionIndex} />
      <OfferPriceDescription
        offer={offer}
        selectedOptionIndex={selectedOptionIndex}
      />
    </VStack>
  )
}

const OfferMultipleOptions = ({
  offer,
  selectedOptionIndex,
  onChangeSelectedOptionIndex,
  reserveMultipleOptionsHeight
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  onChangeSelectedOptionIndex: (index: number) => void
  reserveMultipleOptionsHeight?: boolean
}) => {
  const hasMultipleOptions = (offer.price_options.length || 0) > 1
  const shouldReserveSpace = reserveMultipleOptionsHeight || hasMultipleOptions

  if (!shouldReserveSpace) return null

  if (!hasMultipleOptions) {
    return <Box h={`${OFFER_MULTIPLE_OPTIONS_HEIGHT}px`} w="100%" />
  }

  return (
    <Box h={`${OFFER_MULTIPLE_OPTIONS_HEIGHT}px`} w="100%" display="flex">
      <RadioGroup.Root
        value={selectedOptionIndex.toString()}
        onValueChange={({ value }) =>
          onChangeSelectedOptionIndex(Number(value))
        }
        size="sm"
      >
        <VStack
          gap={2}
          align="start"
          css={{
            "& .chakra-radio__control": {
              height: "1rem !important",
              width: "1rem !important"
            },
            "& .chakra-radio__label": {
              marginLeft: "3px"
            }
          }}
        >
          {offer.price_options.map((option, index) => (
            <RadioGroup.Item key={index} value={index.toString()}>
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText>
                {option.option_label || option.button_label}
              </RadioGroup.ItemText>
            </RadioGroup.Item>
          ))}
        </VStack>
      </RadioGroup.Root>
    </Box>
  )
}

const OfferPriceDescription = ({
  offer,
  selectedOptionIndex
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
}) => {
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasMultipleOptions = (offer.price_options.length || 0) > 1

  if (hasMultipleOptions || !selectedOption.price_description) return null

  return (
    <Box
      m={0}
      lineClamp={1}
      as="span"
      fontSize="sm"
      lineHeight="1 !important"
      fontWeight="bold"
      dangerouslySetInnerHTML={{
        __html: selectedOption.price_description
      }}
    />
  )
}
// OfferBody: Renders the offer description with overflow detection
const OfferBody = ({
  offer,
  maxHeight,
  onMeasureBody,
  forcedBodyHeight
}: {
  offer: CoverPageOffer
  maxHeight?: number
  onMeasureBody?: onMeasureType
  forcedBodyHeight?: number | null
}) => {
  // if (!offer.description) return null
  const effectiveHeight = forcedBodyHeight ?? maxHeight
  const truncate = maxHeight != null

  return (
    <Box
      position="relative"
      h={effectiveHeight ? `${effectiveHeight}px` : "100%"}
      maxH="100%"
      overflow={"hidden"}
      minH={effectiveHeight ? `${effectiveHeight}px` : "0"}
      css={{
        "& .HTMLText": {
          height: "100%"
        },
        "& .TruncatedHTML-more-button": {
          pt: 2
        }
      }}
    >
      <HTMLText
        html={offer.description || ""}
        truncate={truncate}
        truncateProps={
          truncate
            ? {
                maxHeight,
                // ellipsis: true,
                onMeasure: onMeasureBody,
                renderMore: offer.id
                  ? () => (
                      <ImmersiveTriggerButton
                        relativeTo={`offer/${offer.id}/`}
                      />
                    )
                  : undefined
              }
            : undefined
        }
      />
    </Box>
  )
}

// OfferMeta: Renders the access meta information
const OfferMeta = ({ offer }: { offer: CoverPageOffer }) => {
  if (offer.access.meta.length === 0) return null

  return (
    <HStack
      className="OfferMeta"
      gap={1}
      align="stretch"
      justify="center"
      alignItems="center"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      maxW="100%"
      lineHeight="1"
      py="1px"
    >
      {offer.access.meta.map((meta, index) => (
        <Fragment key={`offer-meta-${index}`}>
          {index > 0 && (
            <Text
              as="span"
              fontSize="md"
              mx={0}
              lineHeight="1 !important"
              mt={"-3px"}
            >
              &bull;
            </Text>
          )}
          <Text
            key={index}
            fontSize="sm"
            m={0}
            w="max-content"
            as="span"
            lineHeight="1 !important"
          >
            {meta}
          </Text>
        </Fragment>
      ))}
    </HStack>
  )
}

const OfferConversionMeta = ({ offer }: { offer: CoverPageOffer }) => {
  const { t } = useCoverPageTranslate()
  const { me } = usePathwrightContext()

  return me ? (
    <HStack pos="relative" alignItems="center" zIndex={1} gap={1}>
      <UserAvatar user={me} w="1.2rem" h="1.2rem" />
      <Tooltip
        // NOTE: the offer price_options and meta are not always present. Seems we don't provide them when
        // the user has access to the offer.
        title={[
          ...offer.price_options.map(
            (option) => option.option_label || option.button_label
          ),
          ...offer.access.meta
        ].join(" • ")}
      >
        <Text
          as="span"
          m={0}
          fontSize="sm"
          w="max-content"
          lineHeight="1 !important"
        >
          {t("offer.access.joined_on", {
            defaultValue: "Join on {{date}}",
            date: moment(offer.access.conversion_date).format("MMM D, YYYY")
          })}
        </Text>
      </Tooltip>
    </HStack>
  ) : null
}

export const InspectOffering = ({ offer }: { offer: CoverPageOffer }) => {
  const { t } = useCoverPageTranslate()

  return (
    <Popover.Root positioning={{ placement: "right-end" }}>
      <Popover.Trigger>
        <IconButton
          aria-label={t("offer.inspect_offering", {
            defaultValue: "Inspect Offering"
          })}
          className="inspect-offering-trigger"
          children={<SparklesIcon size="0.9rem" />}
          size="sm"
          variant="ghost"
          position="absolute"
          top={4}
          right={4}
          zIndex={2}
          cursor="pointer"
          opacity={0}
          transition="opacity 0.2s, pointer-events 0.2s"
        />
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            bg="gray.100"
            width="xl"
            top={"5vh"}
            maxW="90vw"
            maxH="90vh"
            overflow="auto"
            p={6}
          >
            <Popover.Arrow />
            <Popover.Body>
              <Text fontSize="sm" fontWeight="bold" mb={2} fontFamily="mono">
                {t("offer.offering_data", { defaultValue: "Offering Data" })}
              </Text>
              <Box
                as="pre"
                fontSize="xs"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                m={0}
              >
                {JSON.stringify(offer, null, 2)}
              </Box>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

// Main offer card component (default export, keep as CoverPageOffer)
type CoverPageOfferCardProps = {
  offer: CoverPageOffer
  showInspectOffering?: boolean
  displayMeta?: boolean
  displayDescription?: boolean
  reserveMultipleOptionsHeight?: boolean
  maxBodyHeightForTruncation?: number
  maxCardHeight?: number // must be a number as it's used in computations
  onMeasureBody?: onMeasureType
  forcedBodyHeight?: number
}

// Dynamic height calculations based on all cards (all in pixels)
export const OFFER_TITLE_HEIGHT = 19.55 // 1.15rem * 17px
export const OFFER_HEADING_SPACER = 8 // 0.5rem * 16px
export const OFFER_PRICE_DESCRIPTION_HEIGHT = 12 // 0.75rem * 16px (sm)
export const OFFER_PRICING_SPACER = 5 // gap
export const OFFER_MULTIPLE_OPTIONS_HEIGHT = 52 // 2 radio rows + inter-row spacing
export const OFFER_META_HEIGHT = 20 // sm line box incl. py (measured ~20px)
export const OFFER_META_SPACER = 5 // gap
export const OFFER_BUTTON_HEIGHT = 47.59 // Button height

const CoverPageOfferCard = ({
  offer,
  showInspectOffering,
  displayMeta,
  displayDescription,
  reserveMultipleOptionsHeight,
  maxCardHeight,
  maxBodyHeightForTruncation,
  onMeasureBody,
  forcedBodyHeight
}: CoverPageOfferCardProps) => {
  const { immersiveActive, offer: currentOffer } = useCoverPageContext()

  const coverPageIsEditable = useCoverPageIsEditable()

  // Selection handling is encapsulated in CoverPageOfferPricing

  // Dynamic height calculations based on all cards (all in pixels)
  const titleHeight = OFFER_TITLE_HEIGHT // 1.15rem * 17px
  const headingSpacer = OFFER_HEADING_SPACER // 0.5rem * 16px
  const priceDescriptionHeight = displayDescription
    ? OFFER_PRICE_DESCRIPTION_HEIGHT
    : 0 // 0.75rem * 16px (sm)
  const pricingSpacer = displayDescription ? OFFER_PRICING_SPACER : 0 // gap
  const metaHeight = displayMeta ? OFFER_META_HEIGHT : 0 // 0.75rem * 16px (sm)
  const metaSpacer = displayMeta ? OFFER_META_SPACER : 0 // gap
  const buttonHeight = OFFER_BUTTON_HEIGHT // Button height
  const hasMultipleOptions = (offer.price_options.length || 0) > 1
  const shouldReserveMultipleOptionsHeight =
    reserveMultipleOptionsHeight || hasMultipleOptions
  const multipleOptionsHeight = shouldReserveMultipleOptionsHeight
    ? OFFER_MULTIPLE_OPTIONS_HEIGHT
    : 0
  const multipleOptionsSpacer = shouldReserveMultipleOptionsHeight
    ? OFFER_PRICING_SPACER
    : 0

  // Calculate bottom section height (divider and pricing)
  const bottomSectionHeight =
    dividerHeight +
    multipleOptionsHeight +
    multipleOptionsSpacer +
    buttonHeight +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  // Calculate available height for body content (accounting for title at top)
  const titleSectionHeight = titleHeight + headingSpacer
  const allPadding = 64 // 2 x Chakra padding 4 (32px) = 64px
  const availableBodyHeight = maxCardHeight
    ? Math.max(
        0,
        maxCardHeight - titleSectionHeight - bottomSectionHeight - allPadding
      )
    : undefined

  const highlightOffer = currentOffer?.id === offer.id && !immersiveActive
  const showConversionMeta = Boolean(offer.access.conversion_date)
  const showBottomMetaRow = offerShowsBottomMetaRow(offer)

  return (
    <LinkBox
      className="CoverPageOfferCardWrapper"
      p={4} // show the box shadow inside the parent item
      cursor={immersiveActive ? "default" : "pointer"}
    >
      <VStack
        gap={0}
        className="CoverPageOfferCard"
        {...augmentationSectionFocusTarget()}
        align="stretch"
        borderRadius="card"
        bg="white"
        p={4} // content padding
        maxH={maxCardHeight ? `${maxCardHeight}px` : "100%"}
        h={"100%"}
        shadow={highlightOffer ? "cardHover" : "card"}
        _hover={{
          shadow: "cardHover",
          "& .inspect-offering-trigger": { opacity: 1 }
        }}
        transition="all 0.2s"
        position="relative"
      >
        {offer && showInspectOffering && <InspectOffering offer={offer} />}

        {coverPageIsEditable && offer && (
          <CoverPageSectionTrigger
            sectionKey="offers"
            panelItemId={offer.id}
            includeLabel={false}
            right={2}
            top={2}
            preventScrollHighlight
            revealWithin=".CoverPageOfferCard"
            itemName={offer.title}
          />
        )}

        {/* Title */}
        <LinkOverlay asChild>
          <Link
            relativeTo={immersiveActive || !offer ? "" : `/offer/${offer.id}/`}
            justifyContent="flex-start"
            textDecor="none"
          >
            <Heading
              lineClamp={1}
              as="h4"
              // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
              size="h7"
              mt={0}
              mb={"8px"}
              maxW="100%"
              className="theme-heading theme-heading-semibold"
              flexShrink={0}
            >
              {offer.title}
            </Heading>
          </Link>
        </LinkOverlay>

        {/* Body - fills available space between title and bottom section */}
        <Box
          className="OfferBodyWrapper"
          top={`${titleSectionHeight}px`}
          left="1rem"
          right="1rem"
          bottom={`${bottomSectionHeight}px`}
          overflow="hidden"
          cursor={"text"}
          onClick={(event) => {
            event.stopPropagation()
          }}
          h={availableBodyHeight ? `${availableBodyHeight}px` : "100%"}
        >
          <OfferBody
            offer={offer}
            maxHeight={immersiveActive ? undefined : maxBodyHeightForTruncation}
            onMeasureBody={onMeasureBody}
            forcedBodyHeight={immersiveActive ? null : forcedBodyHeight}
          />
        </Box>

        {/* Divider */}
        <Box flexShrink={0} h={`${dividerHeight}px`}>
          <CoverPageDivider verticalMargin="10px" horizontalMargin="0px" />
        </Box>

        {/* Bottom section - consistent height across all cards */}
        <VStack
          className="OfferBottomSection"
          flexShrink={0}
          align="center"
          gap={metaSpacer > 0 ? `${metaSpacer}px` : 0}
          h={`${
            multipleOptionsHeight +
            multipleOptionsSpacer +
            buttonHeight +
            priceDescriptionHeight +
            pricingSpacer +
            metaHeight +
            metaSpacer
          }px`}
          justify="flex-start"
        >
          <OfferPricing
            offer={offer}
            reserveMultipleOptionsHeight={shouldReserveMultipleOptionsHeight}
          />
          {showBottomMetaRow &&
            (showConversionMeta ? (
              <OfferConversionMeta offer={offer} />
            ) : (
              <OfferMeta offer={offer} />
            ))}
        </VStack>
      </VStack>
    </LinkBox>
  )
}

export const CoverPageOfferImmersive = ({ id }: { id?: number }) => {
  const { offers } = useCoverPageContext()

  if (!offers?.length) return null

  let offer = offers?.find((offer) => offer.id === Number(id))
  if (!offer) offer = offers[0]

  return (
    <Box
      w="100%"
      h="100%"
      pos="relative"
      px={{ base: 0, sm: "20px", md: "40px" }}
      maxW="800px"
      mx="auto"
    >
      <CoverPageOfferCard
        offer={offer}
        displayMeta={true}
        displayDescription={true}
      />
    </Box>
  )
}

export default CoverPageOfferCard
